
import { SwapHoriz as SwitchIcon } from '@mui/icons-material';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useNovel } from '@/context/novel-context';
import { useUserNovels } from '@/context/user-novels-context';
import { useFirebase } from '@/firebase';
import { useUserProfile } from '@/firebase/hooks/use-user-profile';
import type { CollaboratedNovel } from '@/lib/types';
import { NovelSelection } from '../novel-selection';

interface SidebarProjectSelectorProps {
  collapsed?: boolean;
}

export function SidebarProjectSelector({ collapsed }: SidebarProjectSelectorProps) {
  const { t } = useTranslation();
  const { user } = useFirebase();
  const { selectedNovelId, setSelectedNovelId, novel: currentNovel } = useNovel();
  const navigate = useNavigate();

  const { userData } = useUserProfile();
  const collaboratedNovels = userData.collaboratedNovels;

  const { ownedNovels: novels, isLoading } = useUserNovels();

  const allNovels = useMemo(() => {
    const list: Array<{ id: string; title: string; ownerId: string; role?: string }> = [];
    if (novels) {
      novels.forEach((n) => {
        list.push({ id: n.id, title: n.title, ownerId: user?.uid || '' });
      });
    }
    collaboratedNovels.forEach((collab: CollaboratedNovel) => {
      const key = `${collab.ownerId};${collab.novelId}`;
      list.push({
        id: key,
        title: collab.novelTitle,
        ownerId: collab.ownerId,
        role: collab.role,
      });
    });
    return list;
  }, [novels, collaboratedNovels, user?.uid]);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (id: string) => {
    setSelectedNovelId(id);
    navigate({ to: '/' });
    handleClose();
  };

  if (collapsed) {
    return (
      <Box sx={{ px: 1, py: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
        <Tooltip title={currentNovel?.title || t('sidebar.tooltips.select_novel')} placement="right">
          <IconButton
            size="small"
            onClick={handleClick}
            sx={{
              bgcolor: 'background.default',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1.5,
              width: 40,
              height: 40
            }}
          >
            <SwitchIcon sx={{ fontSize: 20, color: 'primary.main' }} />
          </IconButton>
        </Tooltip>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        >
          {isLoading && <MenuItem disabled>{t('common.loading')}</MenuItem>}
          {allNovels.map((novel) => {
            const roleSuffix = novel.role === 'collaborator' ? ' (Editor)' : novel.role === 'reader' ? ' (Viewer)' : '';
            return (
              <MenuItem
                key={novel.id}
                selected={novel.id === selectedNovelId}
                onClick={() => handleSelect(novel.id)}
              >
                {novel.title}{roleSuffix}
              </MenuItem>
            );
          })}
          <Divider />
          <MenuItem onClick={() => { navigate({ to: '/' }); handleClose(); }}>
            {t('dashboard.selection_grid.title')}
          </MenuItem>
        </Menu>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
      <Typography
        variant="caption"
        sx={{
          color: 'primary.main', // More contrast/branding
          textTransform: 'uppercase',
          fontWeight: 800,
          fontSize: '0.6rem',
          letterSpacing: '0.1em',
          mb: 1.5, // Increased margin
          display: 'block',
          opacity: 0.8
        }}
      >
        {t('header.project')}
      </Typography>
      <Box sx={{ '& .MuiFormControl-root': { width: '100%' } }}>
        <NovelSelection showLabel={false} />
      </Box>
    </Box>
  );
}
